/**
 * algolia-quick-add.js
 *
 * Quick-add-to-cart web component for Algolia InstantSearch product cards.
 *
 * Supports both single-variant and multi-variant products:
 *   - Single available variant  → click "Quick Add · [Size]" → add to cart immediately
 *   - Multiple available variants → click "Quick Add" → size picker expands → pick size → add
 *
 * Variant data is fetched from /products/{handle}.js (the .json endpoint omits
 * the `available` field) and cached in a module-level Map.
 *
 * Theme settings are read from the <algolia-quick-add> element's data attributes,
 * set by the Liquid snippet's {% schema %} block.
 *
 * Wrapped in an IIFE to avoid global lexical scope collisions with other
 * deferred scripts in the Shopify theme.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /*  Caches                                                             */
  /* ------------------------------------------------------------------ */
  const _productCache = new Map();   // handle → Promise<{variants: [{id, option1, available}]}>
  const _addedVariants = new Set();  // variant IDs already added this session

  function fetchProduct(handle) {
    if (_productCache.has(handle)) return _productCache.get(handle);
    const promise = fetch(`${window.Shopify.routes.root}products/${handle}.js`)
      .then(r => r.json())
      .then(product => {
        const variants = product.variants.map(v => ({
          id: v.id,
          option1: v.option1 || '',
          option2: v.option2 || '',
          available: v.available,
          title: v.title,
        }));
        return { variants };
      })
      .catch(err => {
        console.error(`[quick-add] Failed to fetch product for ${handle}`, err);
        _productCache.delete(handle);
        return null;
      });
    _productCache.set(handle, promise);
    return promise;
  }


  /* ------------------------------------------------------------------ */
  /*  Cart helpers                                                       */
  /* ------------------------------------------------------------------ */

  function openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || typeof cartDrawer.open !== 'function') return;
    fetch(`${window.Shopify.routes.root}?sections=cart-drawer`)
      .then(r => r.json())
      .then(sections => {
        const drawerEl = document.getElementById('CartDrawer');
        if (drawerEl && sections['cart-drawer']) {
          const tmp = document.createElement('div');
          tmp.innerHTML = sections['cart-drawer'];
          const newInner = tmp.querySelector('.drawer__inner');
          const curInner = drawerEl.querySelector('.drawer__inner');
          if (newInner && curInner) curInner.innerHTML = newInner.innerHTML;
        }
        cartDrawer.open();
      })
      .catch(() => cartDrawer.open());
  }


  /* ------------------------------------------------------------------ */
  /*  Component                                                          */
  /* ------------------------------------------------------------------ */
  class AlgoliaQuickAdd extends HTMLElement {
    constructor() {
      super();
      this.render = this.render.bind(this);
    }

    /* ---- Read settings from data attributes (set by Liquid schema) -- */
    get settings() {
      if (this._settings) return this._settings;
      this._settings = {
        buttonText:     this.dataset.buttonText || 'Quick Add',
        successText:    this.dataset.successText || 'Added \u2713',
        soldOutText:    this.dataset.soldOutText || 'Sold Out',
        inBagText:      this.dataset.inBagText || 'Already in Bag',
        unavailableText:this.dataset.unavailableText || 'Unavailable',
      };
      return this._settings;
    }

    /* ---- public API called by algolia-hit.js ------------------------ */
    render(item) {
      const s = this.settings;
      const wrapper = document.createElement('div');
      wrapper.classList.add('algolia-quick-add');

      wrapper._item = item;
      wrapper._handle = item.handle;
      wrapper._busy = false;

      const size = item.option1 || '';
      const colorOptions = item.meta?.['trove-variants']?.color_options;
      const currentColor = item.meta?.['trove-product']?.color_display;
      const currentSizes = colorOptions?.[currentColor]?.sizes;
      const isMultiVariant = currentSizes && currentSizes.length > 1;

      // --- Trigger bar ---
      const triggerBar = document.createElement('button');
      triggerBar.classList.add('algolia-quick-add__trigger');
      triggerBar.setAttribute('type', 'button');
      triggerBar.setAttribute('aria-label', `Quick add ${item.title}`);
      triggerBar.textContent = (!isMultiVariant && size) ? `${s.buttonText} \u00B7 ${size}` : s.buttonText;
      triggerBar.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._handleTriggerClick(wrapper, triggerBar);
      });

      // --- Size picker panel (hidden initially) ---
      const panel = document.createElement('div');
      panel.classList.add('algolia-quick-add__panel');
      panel.style.display = 'none';

      wrapper.appendChild(triggerBar);
      wrapper.appendChild(panel);

      // Listen for color selection from the carousel
      wrapper.addEventListener('colorchange', (e) => {
        const { handle, sizes } = e.detail;
        wrapper._handle = handle;
        wrapper._busy = false;
        panel.style.display = 'none';
        panel.innerHTML = '';
        wrapper.classList.remove('algolia-quick-add--open');
        triggerBar.disabled = false;
        triggerBar.className = 'algolia-quick-add__trigger';
        triggerBar.style.display = '';
        triggerBar.textContent = sizes && sizes.length === 1
          ? `${s.buttonText} \u00B7 ${sizes[0]}`
          : s.buttonText;
      });

      return wrapper;
    }

    /* ---- Trigger click: decide single-add vs size picker ------------ */
    async _handleTriggerClick(wrapper, btn) {
      if (wrapper._busy) return;
      wrapper._busy = true;
      wrapper.classList.add('algolia-quick-add--open');

      const origText = btn.textContent;
      btn.textContent = 'Loading\u2026';
      btn.disabled = true;

      try {
        const product = await fetchProduct(wrapper._handle);
        if (!product) {
          this._showTempState(wrapper, btn, origText, this.settings.unavailableText, '--soldout');
          return;
        }

        const available = product.variants.filter(v => v.available);

        if (available.length === 0) {
          this._showTempState(wrapper, btn, origText, this.settings.soldOutText, '--soldout');
          return;
        }

        if (available.length === 1) {
          // Single available variant → add directly
          await this._addVariant(wrapper, btn, available[0], origText);
        } else {
          // Multiple available variants → show size picker
          this._showSizePicker(wrapper, btn, available, origText);
        }
      } catch (err) {
        console.error('[quick-add]', err);
        this._showTempState(wrapper, btn, origText, this.settings.unavailableText, '--error');
      }
    }

    /* ---- Size picker ------------------------------------------------ */
    _showSizePicker(wrapper, triggerBtn, availableVariants, origText) {
      const panel = wrapper.querySelector('.algolia-quick-add__panel');
      panel.innerHTML = '';

      availableVariants.forEach(v => {
        const sizeBtn = document.createElement('button');
        sizeBtn.classList.add('algolia-quick-add__size-btn');
        sizeBtn.setAttribute('type', 'button');
        sizeBtn.textContent = v.option1 || v.title;

        // If already added, mark it
        if (_addedVariants.has(v.id)) {
          sizeBtn.classList.add('algolia-quick-add__size-btn--added');
          sizeBtn.textContent = `${v.option1 || v.title} \u2713`;
        }

        sizeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._addVariant(wrapper, sizeBtn, v, origText, true);
        });
        panel.appendChild(sizeBtn);
      });

      // Hide trigger, show panel
      triggerBtn.style.display = 'none';
      panel.style.display = '';

      // Restore trigger when mouse leaves the card wrapper
      const cardWrapper = wrapper.closest('.card-wrapper') || wrapper.parentElement;
      const restoreHandler = () => {
        // Small delay so the panel doesn't close before a size click registers
        setTimeout(() => {
          if (!wrapper._busy) {
            panel.style.display = 'none';
            triggerBtn.style.display = '';
            triggerBtn.textContent = origText;
            triggerBtn.disabled = false;
            wrapper.classList.remove('algolia-quick-add--open');
          }
        }, 150);
      };
      cardWrapper.addEventListener('mouseleave', restoreHandler, { once: true });

      wrapper._busy = false;
    }

    /* ---- Add a specific variant to cart ------------------------------ */
    async _addVariant(wrapper, btn, variant, origText, isSizeBtn = false) {
      const s = this.settings;

      // Already added this session?
      if (_addedVariants.has(variant.id)) {
        if (isSizeBtn) {
          // Briefly flash "In Bag"
          btn.textContent = s.inBagText;
          btn.classList.add('algolia-quick-add__size-btn--added');
          setTimeout(() => {
            btn.textContent = `${variant.option1 || variant.title} \u2713`;
          }, 1200);
          return;
        }
        this._showTempState(wrapper, btn, origText, s.inBagText, '--success');
        return;
      }

      wrapper._busy = true;
      wrapper.classList.add('algolia-quick-add--open');
      const prevText = btn.textContent;
      btn.textContent = 'Adding\u2026';
      btn.disabled = true;

      try {
        await SHOPIFY_UTILS.addToShopifyCart({ variantId: variant.id });
        _addedVariants.add(variant.id);
        await SHOPIFY_UTILS.refreshCartIconBubble();

        if (isSizeBtn) {
          btn.textContent = `${variant.option1 || variant.title} \u2713`;
          btn.classList.add('algolia-quick-add__size-btn--added');
          btn.disabled = false;
          wrapper._busy = false;
          openCartDrawer();
          return;
        }

        btn.textContent = s.successText;
        btn.classList.add('algolia-quick-add__trigger--success');
        openCartDrawer();

        setTimeout(() => {
          btn.textContent = origText;
          btn.disabled = false;
          btn.classList.remove('algolia-quick-add__trigger--success');
          wrapper._busy = false;
          wrapper.classList.remove('algolia-quick-add--open');
        }, 1500);

      } catch (err) {
        console.error('[quick-add] Add to cart failed:', err);
        const errMsg = err.message || '';

        // Detect inventory/sold-out errors
        const isSoldOut = /sold out|inventory|not enough|no more/i.test(errMsg);

        if (isSizeBtn) {
          btn.textContent = isSoldOut ? s.soldOutText : 'Error';
          btn.classList.add(isSoldOut ? 'algolia-quick-add__size-btn--soldout' : 'algolia-quick-add__size-btn--error');
          btn.disabled = isSoldOut;
          wrapper._busy = false;
          return;
        }

        if (isSoldOut) {
          this._showTempState(wrapper, btn, origText, s.soldOutText, '--soldout');
        } else {
          btn.textContent = 'Error \u2013 Tap to Retry';
          btn.classList.add('algolia-quick-add__trigger--error');
          btn.disabled = false;
          wrapper._busy = false;
        }
      }
    }

    /* ---- Show a temporary state then reset -------------------------- */
    _showTempState(wrapper, btn, origText, text, modifier) {
      const cls = `algolia-quick-add__trigger${modifier}`;
      btn.textContent = text;
      btn.classList.add(cls);
      setTimeout(() => {
        btn.textContent = origText;
        btn.disabled = false;
        btn.classList.remove(cls);
        wrapper._busy = false;
        wrapper.classList.remove('algolia-quick-add--open');
      }, 2000);
    }
  }

  customElements.define('algolia-quick-add', AlgoliaQuickAdd);

})();
